import { ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';
import { getCurrentTenant } from '../config/tenants';
import { useCategories } from '../hooks/useCategories';

function Footer() {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const tenant = getCurrentTenant();

  // Fetch top categories by product count for the footer
  const { data: categoriesData } = useCategories({ limit: 3, sort_by: 'product_count', sort_order: 'desc' });
  const popularCategories = categoriesData?.data?.categories || categoriesData?.categories || [];

  return (
    <footer className="bg-gray-900 text-gray-300 mt-auto">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <ShoppingBag className="w-6 h-6 text-primary-400" aria-hidden="true" />
              <span className="text-lg font-bold text-white">{tenant.name}</span>
            </div>
            <p className="text-sm text-gray-400">
              {t('footerTagline')}
            </p>
          </div>

          {/* Quick Links - Accessibility: nav with aria-label */}
          <nav aria-label="Quick links">
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-primary-300 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/brands" className="hover:text-primary-300 transition-colors">
                  Brands
                </Link>
              </li>
              <li>
                <Link to="/search" className="hover:text-primary-300 transition-colors">
                  Search
                </Link>
              </li>
            </ul>
          </nav>

          {/* Categories - Dynamic from API, fallback to /categories link */}
          <nav aria-label="Popular categories">
            <h3 className="text-white font-semibold mb-4">Popular Categories</h3>
            <ul className="space-y-2 text-sm">
              {popularCategories.length > 0 ? (
                popularCategories.map((cat) => (
                  <li key={cat.id || cat.slug}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="hover:text-primary-300 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li>
                  <Link to="/categories" className="hover:text-primary-300 transition-colors">
                    {language === 'cs' ? 'Všechny kategorie' : 'All Categories'}
                  </Link>
                </li>
              )}
            </ul>
          </nav>

          {/* Legal - Accessibility: nav with aria-label */}
          <nav aria-label="Legal information">
            <h3 className="text-white font-semibold mb-4">{t('legal')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy-policy" className="hover:text-primary-300 transition-colors">
                  {t('privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="hover:text-primary-300 transition-colors">
                  {t('termsConditions')}
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="hover:text-primary-300 transition-colors">
                  {t('cookiePolicy')}
                </Link>
              </li>
              <li>
                <Link to="/imprint" className="hover:text-primary-300 transition-colors">
                  {t('imprint')}
                </Link>
              </li>
            </ul>
          </nav>

          {/* Customer Service - Accessibility: nav with aria-label */}
          <nav aria-label="Customer service">
            <h3 className="text-white font-semibold mb-4">{t('customerService')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/return-policy" className="hover:text-primary-300 transition-colors">
                  {t('returnPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/shipping-policy" className="hover:text-primary-300 transition-colors">
                  {t('shippingPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary-300 transition-colors">
                  {t('contact')}
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} {tenant.name}. {t('allRightsReserved')}.</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
